// Pre-fetch the section HTML for every available variant after window.load
// + idle, so the customer's click-to-fetch later resolves from an
// in-memory cache instead of waiting on a 150-300ms network roundtrip.
//
// IMPORTANT: Shopify section responses carry `Cache-Control: private, no-store`
// (set by the storefront CDN because Set-Cookie is present in the response),
// so the browser's HTTP cache CANNOT hold them. We work around that by
// storing the response text in `window.__variantSectionCache` (a Map keyed
// by request URL). product-info.js's renderProductInfo consults this cache
// before firing its fetch — see the cache-lookup block there.
//
// Companion to assets/product-variant-image-preload.js — that script warms
// the *image* HTTP cache (image responses ARE cacheable). Together they
// make a same-product variant switch effectively free: ~7ms JS critical
// path + setActiveMedia + a Map.get(url) instead of a network roundtrip.
//
// URL format matches product-info.js's buildRequestUrlWithParams:
//   <productUrl>?section_id=<id>&option_values=<option_value_id>
// We mirror it exactly so the in-memory cache key lines up.
//
// Skipped:
//   - Currently-checked radio (its HTML is already on the page)
//   - Disabled radios (variants the customer can't switch to)
//   - Multi-option products (Size × Color etc.) — partial option_values
//     URL would resolve to the wrong variant; combination enumeration is
//     out of scope for now.
//   - Cross-product variants where radio.dataset.productUrl differs from
//     the product-info url — those trigger a full-page fetch.

(function () {
  // Shared in-memory cache. product-info.js reads from this in
  // renderProductInfo before firing its fetch. Initialized before the
  // auto-run guard so a second load of this script (e.g. main-product +
  // homepage-product-switcher coexisting somehow) still produces a
  // working cache map.
  window.__variantSectionCache = window.__variantSectionCache || new Map();

  function preloadVariantSections() {
    var productInfo = document.querySelector('product-info');
    if (!productInfo) return;

    var productUrl = productInfo.dataset.url;
    // Match what product-info.js's sectionId getter returns:
    //   return this.dataset.originalSection || this.dataset.section;
    // so the prefetch URL has the same cache key as the click-time fetch.
    var sectionId = productInfo.dataset.originalSection || productInfo.dataset.section;
    if (!productUrl || !sectionId) return;

    // The variant picker on this theme can be either Dawn's standard
    // <variant-radios>/<variant-selects> (radios with data-option-value-id)
    // or the v2 pill picker (.vp-v2__pill-input with data-option-value-id).
    // Match both. For multi-option products (Size × Color etc.) — out of
    // scope; prefetching would need to enumerate combinations and the
    // partial-option-values URL would resolve to the wrong variant.
    var pickers = document.querySelectorAll('variant-radios, variant-selects');
    if (pickers.length !== 1) return; // single-option only
    var picker = pickers[0];

    var radios = picker.querySelectorAll('input[type="radio"][data-option-value-id]');
    if (radios.length === 0) return;

    radios.forEach(function (radio) {
      if (radio.checked) return;
      if (radio.disabled) return;

      var optionValueId = radio.dataset.optionValueId;
      if (!optionValueId) return;

      // Cross-product variant — skip (would require full-page prefetch).
      // Dawn's standard radios don't carry data-product-url, so this only
      // fires for the v2 picker's cross-product case.
      var radioProductUrl = radio.dataset.productUrl || productUrl;
      if (radioProductUrl !== productUrl) return;

      var url =
        productUrl +
        '?section_id=' +
        encodeURIComponent(sectionId) +
        '&option_values=' +
        encodeURIComponent(optionValueId);

      // Skip if already cached (page-load preload + section:load re-run).
      if (window.__variantSectionCache.has(url)) return;

      // Fetch + store the HTML text in the in-memory cache. HTTP cache can't
      // hold the response (Set-Cookie + Cache-Control: private, no-store)
      // so we keep our own copy. product-info.js's renderProductInfo
      // consults this cache before fetching.
      fetch(url, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (text) {
          // Require <product-info> in the response — guards against
          // password-gated pages, throttling soft-pages, login redirects,
          // and Liquid error fallbacks that return 200 + valid HTML but
          // no section root. A cached non-section response makes the
          // click-time DOMParser.querySelector('product-info') return
          // null, which then null-throws inside viewTransition and
          // leaves the PDP blank for the rest of the session.
          if (text && text.length > 100 && text.includes('<product-info')) {
            window.__variantSectionCache.set(url, text);
          }
        })
        .catch(function () {});
    });
  }

  function schedule() {
    var idle =
      window.requestIdleCallback ||
      function (cb) {
        return setTimeout(cb, 1500);
      };
    // Slightly longer timeout than the image preload — section HTML is
    // bigger per request (100-200KB vs ~50KB) and the customer is less
    // likely to switch variants in the first second after page load.
    idle(preloadVariantSections, { timeout: 4000 });
  }

  // Expose so the homepage product switcher (or anything else that injects
  // a PDP via Section Rendering API after page load) can re-trigger the
  // scan once the embedded <product-info> exists. The window.load auto-run
  // below races against that injection — by the time it fires, the
  // skeleton is still up. Idempotent: preloadVariantSections skips URLs
  // already in __variantSectionCache, so repeat calls only fetch new
  // variants.
  window.__variantSectionPreloadRun = schedule;

  // Auto-run once on initial page load — guarded so a second load of this
  // script on the same page doesn't double-schedule.
  if (window.__variantSectionPreloadStarted) return;
  window.__variantSectionPreloadStarted = true;

  if (document.readyState === 'complete') {
    schedule();
  } else {
    window.addEventListener('load', schedule, { once: true });
  }
})();
